import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PaginationControls, clampPage } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BookOpen, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import { parseErrorMessage } from "@/lib/queryClient";
import remarkGfm from "remark-gfm";
import { ReleaseNoteFull, ReleaseNoteListResponse } from "./types";
import { TASK_BADGE_INLINE_LIMIT } from "./constants";
import { ChangeLogDetailBody } from "./ChangeLogDetailBody";

export function ChangeLogPanel() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [openId, setOpenId] = useState<number | null>(null);
  // Task #861 — when an admin clicks a task badge in the list view we
  // narrow the listing to releases that mention that task ID. Stored
  // separately from the free-text search so we can render an "Active
  // task filter" chip and clear it independently.
  const [taskFilter, setTaskFilter] = useState<number | null>(null);
  const [openTaskPlanId, setOpenTaskPlanId] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, pageSize, taskFilter]);

  // Task #876 — honour `?release=<id>` and `?releaseSha=<sha>` deep links
  // so the Slack release announcement (Task #862) can point straight at a
  // specific release's detail panel instead of the list view. Numeric `id`
  // is set synchronously; SHA needs a server round-trip to resolve to the
  // surrogate id. Bad/missing rows degrade silently — landing on the list
  // is the correct fallback rather than a hard error toast.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const releaseParam = params.get("release");
    const releaseShaParam = params.get("releaseSha");

    if (releaseParam) {
      const parsed = Number.parseInt(releaseParam, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        setOpenId(parsed);
      }
      return;
    }

    if (releaseShaParam) {
      let cancelled = false;
      (async () => {
        try {
          const res = await fetch(
            `/api/admin/changelog/releases/by-sha/${encodeURIComponent(releaseShaParam)}`,
            { credentials: "include" },
          );
          if (!res.ok || cancelled) return;
          const row: ReleaseNoteFull = await res.json();
          if (!cancelled) setOpenId(row.id);
        } catch {
          // Bad deep links shouldn't break the page — drop to list view.
        }
      })();
      return () => { cancelled = true; };
    }
  }, []);

  // Sync openId → URL so a refresh of the deep-linked dialog stays open
  // and so closing the dialog cleans up the params (rather than leaving a
  // stale `?releaseSha=` lying around). We canonicalise both forms to
  // `?release=<id>` once the SHA has been resolved so the URL doesn't
  // double-encode the same intent.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (openId == null) {
      if (!params.has("release") && !params.has("releaseSha")) return;
      params.delete("release");
      params.delete("releaseSha");
    } else {
      const alreadyCanonical =
        params.get("release") === String(openId) && !params.has("releaseSha");
      if (alreadyCanonical) return;
      params.set("release", String(openId));
      params.delete("releaseSha");
    }
    const nextSearch = params.toString();
    const newUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
    window.history.replaceState({}, "", newUrl);
  }, [openId]);

  const offset = (page - 1) * pageSize;
  const listQuery = useQuery<ReleaseNoteListResponse>({
    queryKey: ["/api/admin/changelog/releases", { q: debouncedSearch, taskId: taskFilter, limit: pageSize, offset }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (taskFilter != null) params.set("taskId", String(taskFilter));
      params.set("limit", String(pageSize));
      params.set("offset", String(offset));
      const res = await fetch(`/api/admin/changelog/releases?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load change log");
      return res.json();
    },
  });

  // Task #861 — when an admin clicks a task badge we open the project-task
  // plan inline. Lazy-loaded; reuses the same markdown viewer as the
  // changelog detail dialog.
  // Task #890 — Plans now come from the `task_plans` DB table snapshotted
  // by the deploy-build hook (`scripts/changelog/snapshot-task-plans.ts`),
  // with a filesystem fallback for local dev. The `source` field tells
  // admins which one served the response.
  const taskPlanQuery = useQuery<{
    taskId: number;
    title: string | null;
    content: string | null;
    source?: "db" | "file" | null;
    updatedAt?: string | null;
  }>({
    queryKey: ["/api/admin/changelog/task-plan", openTaskPlanId],
    enabled: openTaskPlanId != null,
    queryFn: async () => {
      const res = await fetch(`/api/admin/changelog/task-plan/${openTaskPlanId}`, { credentials: "include" });
      if (!res.ok) {
        const body: { message?: string } = await res.json().catch(() => ({}));
        throw new Error(body?.message || "Failed to load task plan");
      }
      return res.json();
    },
  });

  const total = listQuery.data?.total ?? 0;
  useEffect(() => {
    setPage((p) => clampPage(p, total, pageSize));
  }, [total, pageSize]);

  const detailQuery = useQuery<ReleaseNoteFull>({
    queryKey: ["/api/admin/changelog/releases", openId],
    enabled: openId != null,
    queryFn: async () => {
      const res = await fetch(`/api/admin/changelog/releases/${openId}`, { credentials: "include" });
      if (!res.ok) {
        const body: { message?: string } = await res.json().catch(() => ({}));
        throw new Error(body?.message || "Failed to load release");
      }
      return res.json();
    },
  });

  useEffect(() => {
    if (detailQuery.error) {
      toast({
        title: "Couldn't load release",
        description: parseErrorMessage(detailQuery.error, "Unknown error"),
        variant: "destructive",
      });
    }
  }, [detailQuery.error, toast]);

  const rows = listQuery.data?.rows ?? [];

  return (
    <div className="space-y-4" data-testid="panel-changelog">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Change Log</h2>
          <p className="text-sm text-muted-foreground">
            Every published release, recorded automatically by the deploy build hook (Task #855). Click View to read the full release notes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {taskFilter != null && (
            <Badge
              variant="default"
              className="h-8 cursor-pointer px-2 py-1 text-xs"
              onClick={() => setTaskFilter(null)}
              data-testid="badge-changelog-active-task-filter"
            >
              Task #{taskFilter} ×
            </Badge>
          )}
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, summary, body, or SHA…"
            className="h-8 w-[260px] text-sm"
            data-testid="input-changelog-search"
          />
        </div>
      </div>

      {listQuery.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : listQuery.error ? (
        <p className="text-sm text-destructive" data-testid="text-changelog-error">
          Failed to load: {(listQuery.error as Error)?.message ?? "Unknown error"}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="text-changelog-empty">
          {debouncedSearch ? "No releases match this search." : "No releases recorded yet."}
        </p>
      ) : (
        <>
          <Table data-testid="table-changelog">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Published</TableHead>
                <TableHead className="w-[160px]">Author</TableHead>
                <TableHead>Title & tasks</TableHead>
                <TableHead className="w-[120px]">Counts</TableHead>
                <TableHead className="w-[110px]">SHA</TableHead>
                <TableHead className="w-[90px]">Source</TableHead>
                <TableHead className="w-[160px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const shortSha = row.deployCommitSha.slice(0, 8);
                const authorName = row.deployAuthorName || row.deployAuthorEmail || "—";
                const inlineTaskIds = row.taskIds.slice(0, TASK_BADGE_INLINE_LIMIT);
                const overflowCount = Math.max(0, row.taskIds.length - inlineTaskIds.length);
                return (
                  <TableRow key={row.id} data-testid={`row-changelog-${row.id}`}>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`text-changelog-published-${row.id}`}>
                      {new Date(row.publishedAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`text-changelog-author-${row.id}`}>
                      <div className="font-medium">{authorName}</div>
                      {row.contributors.length > 1 && (
                        <div className="text-muted-foreground" data-testid={`text-changelog-cocontribs-${row.id}`}>
                          + {row.contributors.length - 1} other{row.contributors.length - 1 === 1 ? "" : "s"}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium" data-testid={`text-changelog-title-${row.id}`}>
                      {row.title}
                      {row.summary ? (
                        <div className="text-xs text-muted-foreground font-normal mt-0.5" data-testid={`text-changelog-summary-${row.id}`}>
                          {row.summary}
                        </div>
                      ) : null}
                      {row.taskIds.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`group-changelog-task-badges-${row.id}`}>
                          {inlineTaskIds.map((tid) => {
                            const title = row.taskTitles?.[String(tid)];
                            const label = title ? `#${tid} · ${title}` : `#${tid}`;
                            return (
                              <Badge
                                key={tid}
                                variant="outline"
                                className="cursor-pointer max-w-[260px] truncate text-xs hover:bg-accent"
                                title={title ? `Task #${tid} — ${title}` : `Task #${tid}`}
                                onClick={() => {
                                  setTaskFilter(tid);
                                  setOpenTaskPlanId(tid);
                                }}
                                data-testid={`badge-changelog-task-${row.id}-${tid}`}
                              >
                                {label}
                              </Badge>
                            );
                          })}
                          {overflowCount > 0 && (
                            <Badge
                              variant="outline"
                              className="cursor-pointer text-xs hover:bg-accent"
                              onClick={() => setOpenId(row.id)}
                              data-testid={`badge-changelog-task-more-${row.id}`}
                            >
                              +{overflowCount} more
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-xs" data-testid={`badge-changelog-commits-${row.id}`}>
                          {row.commitCount} commit{row.commitCount === 1 ? "" : "s"}
                        </Badge>
                        <Badge variant="outline" className="text-xs" data-testid={`badge-changelog-files-${row.id}`}>
                          {row.fileCount} file{row.fileCount === 1 ? "" : "s"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-mono" data-testid={`text-changelog-sha-${row.id}`}>
                      {shortSha}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" data-testid={`badge-changelog-source-${row.id}`}>{row.source}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setOpenId(row.id)}
                          data-testid={`button-view-changelog-${row.id}`}
                        >
                          <BookOpen className="mr-1 h-4 w-4" />
                          View
                        </Button>
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          data-testid={`button-download-changelog-${row.id}`}
                        >
                          <a
                            href={`/api/admin/changelog/releases/${row.id}/download`}
                            download
                          >
                            .md
                          </a>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <PaginationControls
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testIdPrefix="changelog"
          />
        </>
      )}

      <Dialog open={openId != null} onOpenChange={(o) => { if (!o) setOpenId(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-changelog-detail">
          <DialogHeader>
            <DialogTitle data-testid="text-changelog-detail-title">
              {detailQuery.data?.title ?? "Release"}
            </DialogTitle>
            {detailQuery.data && (
              <DialogDescription data-testid="text-changelog-detail-meta">
                <span className="font-mono">{detailQuery.data.deployCommitSha.slice(0, 12)}</span>
                {detailQuery.data.deployAuthorName ? <> · deployed by {detailQuery.data.deployAuthorName}</> : null}
                {detailQuery.data.summary ? <> · {detailQuery.data.summary}</> : null}
              </DialogDescription>
            )}
          </DialogHeader>
          {detailQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-4 w-full" />)}
            </div>
          ) : detailQuery.error ? (
            <p className="text-sm text-destructive" data-testid="text-changelog-detail-error">
              {(detailQuery.error as Error)?.message ?? "Unknown error"}
            </p>
          ) : detailQuery.data ? (
            <ChangeLogDetailBody
              detail={detailQuery.data}
              onTaskClick={(tid) => {
                setTaskFilter(tid);
                setOpenTaskPlanId(tid);
              }}
            />
          ) : null}
          {/* Task #899 — admins were hand-crafting the deep-link URL when
              sharing a release in Slack/email/PR. The button copies the
              canonical `/admin?tab=changelog&release=<id>` form (matching
              what the URL-sync effect above writes), so paste-back also
              re-opens this dialog. */}
          {openId != null && (
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (typeof window === "undefined") return;
                  const url = `${window.location.origin}/admin?tab=changelog&release=${openId}`;
                  // Confirm only on a successful clipboard write so we don't
                  // surface a false-positive "copied" toast in browsers that
                  // block clipboard access (e.g. Safari without a user
                  // gesture, or insecure contexts).
                  try {
                    if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
                    await navigator.clipboard.writeText(url);
                    toast({ title: "Link copied", description: "Deep link to this release copied to clipboard." });
                  } catch {
                    toast({
                      title: "Couldn't copy link",
                      description: "Copy this URL manually instead: " + url,
                      variant: "destructive",
                    });
                  }
                }}
                data-testid="button-copy-changelog-link"
              >
                <Copy className="mr-2 h-3.5 w-3.5" />
                Copy link
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Task #861 — task plan viewer. Triggered when an admin clicks a
          task badge so they can see what shipped without leaving the
          panel.
          Task #890 — Reads from the `task_plans` DB table snapshotted at
          deploy build time, falling back to the local `.local/tasks/`
          tree only in dev. The empty-state message reflects that older
          tasks (created before this snapshot system existed) may simply
          have no recorded plan. */}
      <Dialog open={openTaskPlanId != null} onOpenChange={(o) => { if (!o) setOpenTaskPlanId(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-changelog-task-plan">
          <DialogHeader>
            <DialogTitle data-testid="text-changelog-task-plan-title">
              {taskPlanQuery.data?.title
                ? `Task #${taskPlanQuery.data.taskId} — ${taskPlanQuery.data.title}`
                : openTaskPlanId != null
                  ? `Task #${openTaskPlanId}`
                  : "Task"}
            </DialogTitle>
            <DialogDescription data-testid="text-changelog-task-plan-source">
              {taskPlanQuery.data?.source === "db" && taskPlanQuery.data?.updatedAt
                ? <>Project task plan · snapshotted {new Date(taskPlanQuery.data.updatedAt).toLocaleString()}</>
                : taskPlanQuery.data?.source === "file"
                  ? <>Project task plan · live from <span className="font-mono">.local/tasks/task-{openTaskPlanId}.md</span></>
                  : <>Project task plan</>}
            </DialogDescription>
          </DialogHeader>
          {taskPlanQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-4 w-full" />)}
            </div>
          ) : taskPlanQuery.error ? (
            <p className="text-sm text-destructive" data-testid="text-changelog-task-plan-error">
              {(taskPlanQuery.error as Error)?.message ?? "Unknown error"}
            </p>
          ) : taskPlanQuery.data?.content ? (
            <article
              className="prose prose-sm dark:prose-invert max-w-none"
              data-testid="text-changelog-task-plan-body"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{taskPlanQuery.data.content}</ReactMarkdown>
            </article>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="text-changelog-task-plan-empty">
              No plan recorded for this task. Older tasks shipped before plans were snapshotted into the database, so their bodies aren't recoverable from the deployed app.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
